import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface ImpersonateButtonProps {
  entityType: "ORGANIZATION" | "BUSINESS_UNIT" | "CUSTOMER";
  entityId: string;
  entityName: string;
  variant?: "default" | "outline" | "ghost" | "premium";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  showLabel?: boolean;
}

export function ImpersonateButton({
  entityType,
  entityId,
  entityName,
  variant = "outline",
  size = "default",
  className,
  showLabel = true,
}: ImpersonateButtonProps) {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  // Only show for SUPER admins
  if (user?.role !== "SUPER") {
    return null;
  }

  const impersonateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/admin/impersonate", {
        entityType,
        entityId,
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to impersonate");
      }
      return response.json();
    },
    onSuccess: async (data) => {
      await refreshUser();
      queryClient.invalidateQueries({ queryKey: ["impersonation-state"] });
      queryClient.invalidateQueries();
      toast({
        title: "Impersonation Active",
        description: `Now viewing as ${data.entityName || entityName}`,
      });
      // Navigate to customer portal when impersonating a customer
      if (entityType === "CUSTOMER") {
        setLocation("/portal");
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Impersonation Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const entityLabel = entityType.replace("_", " ").toLowerCase();

  return (
    <Button
      variant={variant}
      size={size}
      className={cn("gap-2", className)}
      onClick={() => impersonateMutation.mutate()}
      disabled={impersonateMutation.isPending}
    >
      <Eye className="h-4 w-4" />
      {showLabel && (
        <span>
          {impersonateMutation.isPending
            ? "Starting..."
            : `View as this ${entityLabel}`}
        </span>
      )}
    </Button>
  );
}
